import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { API_ROUTES, API_DOMAIN } from '../../../Reusable_Components/Config/apiConfig';
import { useFormDraft } from '../../../../hooks/useFormDraft';
import DateTimePicker from '../../../Reusable_Components/Template_Components/DateTimePicker';
import { fmtDateTime } from '../../../../utils/fmt';

function buildHeaders() {
  const token = localStorage.getItem('customer_management_token');
  const tenantId = localStorage.getItem('crm_tenant_id');
  const tenantToken = localStorage.getItem('crm_tenant_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Token ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-ID': tenantId } : {}),
    ...(tenantToken ? { 'X-Tenant-Token': tenantToken } : {}),
  };
}

const PRESETS = [
  { label: '15 min', minutes: 15 },
  { label: '30 min', minutes: 30 },
  { label: '1 hour', minutes: 60 },
  { label: '3 hours', minutes: 180 },
  { label: '1 day', minutes: 1440 },
  { label: '3 days', minutes: 4320 },
  { label: '1 week', minutes: 10080 },
];

const pad = (n) => String(n).padStart(2, '0');

const toDatetimeLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};


const formatLead = (minutes) => {
  if (minutes < 0) return `${Math.abs(minutes)} min after activity`;
  if (minutes < 60) return `${minutes} min before activity`;
  if (minutes < 1440) return `${Math.round(minutes / 60)} hr before activity`;
  return `${Math.round(minutes / 1440)} day(s) before activity`;
};

const EMPTY = { activity: '', reminder_time: '', notified: false };

const ReminderInput = () => {
  const { reminderId = '' } = useParams();
  const navigate = useNavigate();
  const decodedId = reminderId ? atob(reminderId) : reminderId;
  const id = decodedId.replace(/\D/g, '');
  const isEditing = !!id;

  const [activities, setActivities] = useState([]);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { saveDraft, loadDraft, clearDraft } = useFormDraft('customerReminder', id || null);

  // Fetch activities fresh every mount
  useEffect(() => {
    fetch(`${API_DOMAIN}${API_ROUTES.customerActivity}?page_size=200`, { headers: buildHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => setActivities(Array.isArray(data) ? data : (data.results || [])))
      .catch(err => console.error('Failed to load activities:', err));
  }, []);

  // Load existing reminder (edit) or draft (create)
  useEffect(() => {
    if (isEditing) {
      axios.get(`${API_DOMAIN}${API_ROUTES.customerReminder}${id}/`, { headers: buildHeaders() })
        .then(res => {
          const d = res.data;
          const actId = typeof d.activity === 'object' ? d.activity?.id : d.activity;
          setForm({
            activity: actId != null ? String(actId) : '',
            reminder_time: d.reminder_time ? toDatetimeLocal(d.reminder_time) : '',
            notified: d.notified || false,
          });
        })
        .catch(err => console.error('Error fetching reminder:', err));
    } else {
      const draft = loadDraft();
      if (draft) setForm(prev => ({ ...prev, ...draft }));
    }
  }, [id, isEditing]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync activity preview card
  useEffect(() => {
    if (form.activity && activities.length) {
      setSelectedActivity(activities.find(a => String(a.id) === String(form.activity)) || null);
    } else {
      setSelectedActivity(null);
    }
  }, [form.activity, activities]);

  const update = useCallback((field, value) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (!isEditing) saveDraft(next);
      return next;
    });
  }, [isEditing, saveDraft]);

  const applyPreset = (minutes) => {
    if (!selectedActivity?.scheduled_date) return;
    const t = new Date(new Date(selectedActivity.scheduled_date).getTime() - minutes * 60000);
    update('reminder_time', toDatetimeLocal(t.toISOString()));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.activity || !form.reminder_time) {
      setError('Activity and reminder time are required.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        activity: Number(form.activity),
        reminder_time: new Date(form.reminder_time).toISOString(),
        notified: form.notified,
      };
      if (isEditing) {
        await axios.put(`${API_DOMAIN}${API_ROUTES.customerReminder}${id}/`, payload, { headers: buildHeaders() });
      } else {
        await axios.post(`${API_DOMAIN}${API_ROUTES.customerReminder}`, payload, { headers: buildHeaders() });
      }
      clearDraft();
      setSuccess(isEditing ? 'Reminder updated.' : 'Reminder created successfully.');
      setTimeout(() => navigate('/customer/reminder/list'), 1400);
    } catch (err) {
      setError('Failed to save reminder. Please try again.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const leadMinutes =
    selectedActivity?.scheduled_date && form.reminder_time
      ? Math.round((new Date(selectedActivity.scheduled_date) - new Date(form.reminder_time)) / 60000)
      : null;

  return (
    <div style={{ maxWidth: 640 }}>
      <h1 style={{ marginBottom: 4 }}>{isEditing ? 'Edit Reminder' : 'New Reminder'}</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 24 }}>
        Attach a reminder to a scheduled activity and choose when to be notified.
      </p>

      <form
        className="card"
        style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 22 }}
        onSubmit={handleSubmit}
      >
        {/* ── Activity selector ── */}
        <div className="form-group">
          <label className="form-label">
            Activity <span style={{ color: 'var(--color-danger)' }}>*</span>
          </label>
          <select
            className="form-input"
            value={form.activity}
            onChange={e => update('activity', e.target.value)}
            required
          >
            <option value="">Select an activity…</option>
            {activities.map(a => (
              <option key={a.id} value={a.id}>
                {a.subject || `Activity #${a.id}`}
                {a.scheduled_date ? `  ·  ${fmtDateTime(a.scheduled_date)}` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* ── Activity preview ── */}
        {selectedActivity && (
          <div style={{
            background: 'var(--color-primary-pale)',
            border: '1px solid var(--color-primary-light, #c7d7f5)',
            borderRadius: 8, padding: '14px 18px',
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 28px',
            fontSize: 12,
          }}>
            <div>
              <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Subject</div>
              <strong style={{ fontSize: 13 }}>{selectedActivity.subject}</strong>
            </div>
            {selectedActivity.scheduled_date && (
              <div>
                <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Scheduled</div>
                <strong>{fmtDateTime(selectedActivity.scheduled_date)}</strong>
              </div>
            )}
            {selectedActivity.location && (
              <div>
                <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Location</div>
                <strong>{selectedActivity.location}</strong>
              </div>
            )}
            {selectedActivity.duration_minutes && (
              <div>
                <div style={{ color: 'var(--text-muted)', marginBottom: 2 }}>Duration</div>
                <strong>{selectedActivity.duration_minutes} min</strong>
              </div>
            )}
          </div>
        )}

        {/* ── Time presets (only when activity has a scheduled date) ── */}
        {selectedActivity?.scheduled_date && (
          <div className="form-group">
            <label className="form-label" style={{ marginBottom: 8 }}>Notify me before</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {PRESETS.map(p => (
                <button
                  key={p.label}
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => applyPreset(p.minutes)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Reminder time ── */}
        <div className="form-group">
          <label className="form-label">
            Reminder Time <span style={{ color: 'var(--color-danger)' }}>*</span>
            {leadMinutes !== null && (
              <span style={{
                marginLeft: 10, fontSize: 11, fontWeight: 600,
                color: leadMinutes < 0 ? 'var(--color-danger)' : 'var(--color-primary)',
              }}>
                {formatLead(leadMinutes)}
              </span>
            )}
          </label>
          <DateTimePicker
            value={form.reminder_time}
            onChange={e => update('reminder_time', e.target.value)}
            dateConstraint="future"
            required
          />
          {leadMinutes !== null && leadMinutes < 0 && (
            <p style={{ fontSize: 11, color: 'var(--color-danger)', margin: '4px 0 0' }}>
              This reminder is set after the activity's scheduled time.
            </p>
          )}
        </div>

        {/* ── Notification status (edit mode) ── */}
        {isEditing && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span className="form-label" style={{ margin: 0 }}>Notification Status</span>
            <button
              type="button"
              onClick={() => update('notified', !form.notified)}
              style={{
                padding: '4px 16px', borderRadius: 99, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, transition: 'all 0.15s',
                background: form.notified ? '#22c55e22' : '#f59e0b22',
                color: form.notified ? '#22c55e' : '#f59e0b',
                outline: `1px solid ${form.notified ? '#22c55e40' : '#f59e0b40'}`,
              }}
            >
              {form.notified ? '✓ Notified' : '⏳ Pending'}
            </button>
          </div>
        )}

        {/* ── Feedback ── */}
        {error && (
          <p style={{ color: 'var(--color-danger)', fontSize: 13, margin: 0 }}>{error}</p>
        )}
        {success && (
          <p style={{ color: '#22c55e', fontSize: 13, margin: 0 }}>{success}</p>
        )}

        {/* ── Actions ── */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 4 }}>
          {!isEditing && (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => { setForm(EMPTY); clearDraft(); }}
            >
              Reset
            </button>
          )}
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving…' : isEditing ? 'Update Reminder' : 'Create Reminder'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReminderInput;
